/**
 * Claimed intake — the ACCOUNT LINK SNAPSHOT contract (docs/29 §Linking (Plaid), snapshot
 * mode; crossmarc/steward-app#1014, decided by Matt 2026-09-03).
 *
 * A prospect who has claimed their draft may connect an account instead of typing balances
 * in. The kept-link Lambda exchanges the public token, reads balances and holdings, downloads
 * the most recent statement per account, and KEEPS the Item: its access token is stored
 * encrypted in the kept-connection row, with a `removeAfter` date, until the person removes it
 * in the form or the kept-remover ends it. Nothing reads from the bank while it is kept (no
 * refresh is run by this app; Plaid refreshes its own copy). Nothing in this module or in the
 * browser-facing code downstream of it holds a Plaid access token or item id: the snapshot
 * below is what the form and the advisor see, next to the statement PDFs themselves.
 *
 * This module holds every value the four processes involved must agree on:
 *
 *   • the SHAPE of the snapshot the reader produces, the SPA saves and the writer stores;
 *   • the bounds on every field of it, because the snapshot reaches the draft row through the
 *     ORDINARY client-echoed save path (see below) and is therefore caller text by the time
 *     the writer sees it;
 *   • the per-exchange statement cap, which is what keeps Plaid's per-Item Statements bill
 *     bounded by code rather than by intention.
 *
 * ## 🚨 The snapshot is CLIENT-ECHOED, and that is a deliberate trade
 *
 * The reader computes it, hands it to the browser, and the browser saves it through
 * `PUT /intake/{firmSlug}/drafts/{draftId}` like any other answer. So a caller holding a
 * session can store any snapshot this schema admits **about their own draft**. That is the
 * same bargain the document reference makes and for the same reason: the
 * alternative is a second writer of the draft row, racing the one the prospect's own saves go
 * through, which docs/29 has refused twice. What bounds it is this schema — counts, charsets
 * and numeric ranges — and the fact that a snapshot is a metadata claim about a prospect's own
 * disclosure that reaches no key, no credential and no other draft.
 *
 * ⚠️ **A snapshot is therefore evidence of what a prospect told us, not proof of what a bank
 * said.** Nothing downstream may treat it as a custodial figure. It exists so an adviser
 * opening a proposal has real numbers to work from, alongside the statement PDFs, which ARE
 * the bank's own document.
 *
 * ## What is NOT here
 *
 * The Plaid access token, the item id, the institution id, a field for the account numbers, and the
 * consent copy. The first two never leave the reader's invocation; the next two are Plaid
 * vocabulary a family has no use for; the copy lives on the SPA panel that shows it, where it
 * is reviewed as copy.
 */

import { z } from 'zod';

/**
 * The most recent statements ONE exchange may download.
 *
 * 🚨 **A cost control before it is anything else.** Plaid bills Statements per Item at
 * creation, by the number of statements the Link token's date range implies, plus per refresh
 * — and this feature never refreshes. The date range the reader sends is one month, so an
 * ordinary Item yields one statement per account; this cap is what bounds the pathological
 * case (an Item with twenty accounts) to something a prospect's proposal actually needs and a
 * firm's bill can absorb.
 *
 * It is also the only thing standing between one exchange and twenty PDF transfers inside a
 * 29-second Lambda. See the reader's deadline for the other half of that.
 */
export const INTAKE_LINK_MAX_STATEMENTS = 4;

/** The most accounts one snapshot may carry. Generous for a household, far under anything
 *  that would make the draft row large. */
export const INTAKE_SNAPSHOT_MAX_ACCOUNTS = 50;

/**
 * The most holdings one snapshot may carry.
 *
 * A brokerage account with more positions than this is real, and the snapshot says so rather
 * than pretending otherwise — see {@link IntakeDraftSnapshotSchema}'s `holdingsTruncated`. The
 * cap exists because the draft row is a single DynamoDB item behind appcore, and a snapshot is
 * only ever the input to a conversation with an adviser.
 */
export const INTAKE_SNAPSHOT_MAX_HOLDINGS = 100;

/** The most institutions one draft's snapshot may name — a prospect may link more than once,
 *  and each link merges into the same snapshot. */
export const INTAKE_SNAPSHOT_MAX_INSTITUTIONS = 10;

/** The longest display string any snapshot field may carry. */
export const INTAKE_SNAPSHOT_TEXT_MAX = 120;

/**
 * The charset a snapshot display string may use: anything but control characters and the two
 * noncharacters SQS rejects.
 *
 * Byte-for-byte the rule `intakeStatements.ts` applies to a prospect's filename, and for the
 * same reasons: an account is called what the bank calls it, in whatever script that is, and
 * what breaks the things this value passes through is C0/C1 controls (a newline forges a JSON
 * log line and a CSV row) plus the two noncharacters the draft queue refuses.
 */
const SNAPSHOT_TEXT_SAFE = /^[^\u0000-\u001F\u007F-\u009F\uFFFE\uFFFF]+$/u;

/** One bounded display string. */
const SnapshotText = z.string().trim().min(1).max(INTAKE_SNAPSHOT_TEXT_MAX).regex(SNAPSHOT_TEXT_SAFE);

/**
 * The magnitude bound a money figure on this channel may carry, in either direction.
 * Exported so a second parser of a money-shaped value — `intakePrefill.ts`'s
 * `parseStrictMoney`, steward-app#1490 — can reuse the SAME bound rather than picking its
 * own, which is what let a 100-character all-nines intake string parse to `1e100` and
 * reach `marketValue`/`currentBalance` unbounded (the #738 class: `advisor.ts` ~3494 has
 * the account of a similar figure overflowing `Number.MAX_VALUE` to `Infinity`,
 * serialising to `null`, and rendering as $0).
 */
export const SNAPSHOT_AMOUNT_MAGNITUDE_BOUND = 1e12;

/**
 * A money figure, bounded on both sides.
 *
 * `finite()` refuses `Infinity` and `NaN`, which `JSON.parse` cannot produce but a hand-built
 * message could. The magnitude bound is a trillion: past that the value is not a balance, and
 * a number a page will format has no business being unbounded.
 */
const SnapshotAmount = z.number().finite().min(-SNAPSHOT_AMOUNT_MAGNITUDE_BOUND).max(SNAPSHOT_AMOUNT_MAGNITUDE_BOUND);

/**
 * Plaid's own `account_id`, and the ONLY provider identifier this contract carries.
 *
 * 🚨 It is opaque, per-Item, and useless once the Item is removed — which it always is by the
 * time this value is stored. It is kept so holdings can be shown under the account they belong
 * to, and for nothing else. Charset-pinned because it is compared and rendered; it never
 * becomes a path segment, a key, or a policy component.
 */
const SnapshotAccountId = z.string().min(1).max(64).regex(/^[A-Za-z0-9_-]+$/);

/** {@link SnapshotAccountId}, exported for the two other contracts that name an account by it
 *  (crossmarc/steward-app#1710): a client's removal of a connected account (`intakeRemoval.ts`),
 *  and the account a Plaid-pulled statement record came from (`intakeStatements.ts`). */
export const IntakeSnapshotAccountIdSchema = SnapshotAccountId;

/** An ISO 4217 code, as Plaid spells it. */
const SnapshotCurrency = z.string().regex(/^[A-Z]{3}$/);

/**
 * ONE account, as the snapshot records it.
 *
 * `strictObject`, like every other schema on this channel: an undeclared key is a rejection
 * rather than a silent strip, so a future field cannot arrive from a browser before the
 * process that reads it exists.
 *
 * ⚠️ **What this schema carries of an account's identity, read 2026-10-07.** It has no
 * account-number field and no routing-number field. The link-token request does not ask Plaid
 * for the `auth` product (the product that returns account and routing numbers): the snapshot's
 * token takes `linkProductShape(input.now)` with no overrides (`createIntakeLinkToken`,
 * `services/intake-reader/src/plaidSnapshot.ts`), whose defaults name `transactions`,
 * `investments`, `liabilities` and `statements` (`packages/account-aggregation/src/sources/plaid.ts`),
 * and `plaidSnapshot.test.ts` asserts the request holds no `auth` or `identity`. What identifies an
 * account here is `mask`, which is bounded to at most 8 letters and digits and is echoed from the
 * prospect's browser, so it can hold a whole account number at a bank whose numbers are that
 * short. Showing the stored mask to a firm at any stored length is a founder decision
 * (crossmarc/steward-app#2165, 2026-10-07); `intakeFirmView.ts` records it.
 */
export const IntakeSnapshotAccountSchema = z.strictObject({
  accountId: SnapshotAccountId,
  /**
   * The institution this account was pulled from, when the pull could name one.
   *
   * 🚨 **This is the only STABLE identity an account has across two links, and that is what it
   * is for.** `accountId` is Plaid's, and Plaid's account ids are ITEM-scoped: this design
   * creates a fresh Item on every link (kept until removed, not removed before the call answers), so the same real-world
   * account comes back with a DIFFERENT `accountId` the second time a prospect connects the same
   * bank. Deduplicating on `accountId` alone would therefore accumulate a duplicate copy of a
   * portfolio every time somebody pressed Connect twice, and hand an adviser a proposal with the
   * balances counted twice. The SPA's merge replaces a stored institution's accounts wholesale
   * when a link names the same institution again — see `useClaimedDraft.mergeSnapshot`.
   *
   * ⚠️ **Optional, and its absence is a real state rather than a gap to fill.** The institution
   * name comes from the `/statements/list` response, so an Item whose institution is outside
   * Plaid's Statements coverage has none. A re-link of THAT institution falls back to the
   * `accountId` comparison and can duplicate; the accounts also carry `statementsUnavailable`,
   * so a prospect in that state has already been asked to upload instead.
   */
  institution: SnapshotText.optional(),
  /** What the bank or the prospect calls it. */
  name: SnapshotText,
  /** What Plaid's `mask` is expected to hold: the last characters the bank displays (a reading of
   *  Plaid's behaviour not checked against its documentation). This rule admits at most 8 letters
   *  and digits, chosen by the prospect's browser; it does not keep an account number out. */
  mask: z.string().max(8).regex(/^[A-Za-z0-9]*$/).optional(),
  /** Plaid's account type (`depository`, `investment`, `credit`, `loan`, `brokerage`, `other`). */
  type: z.string().min(1).max(32).regex(/^[a-z0-9_]+$/),
  /** Plaid's subtype (`checking`, `401k`, …). Absent on an account Plaid could not classify. */
  subtype: z.string().max(48).regex(/^[a-z0-9_ .-]*$/).optional(),
  currency: SnapshotCurrency.optional(),
  /**
   * The current balance. `null` where the institution did not supply one, which Plaid does
   * return — a missing balance is a fact about the account and not a zero.
   */
  balance: SnapshotAmount.nullable(),
  /** The available balance, where the institution supplies one. */
  available: SnapshotAmount.nullable().optional(),
  /**
   * 🚨 No statement could be pulled for this account, so the form asks for an upload instead.
   *
   * ⚠️ It CONFLATES two causes, on purpose: the institution does not support Plaid Statements
   * (coverage is per institution, and Statements is US only), and the institution supports it
   * but posted nothing inside the one-month window the Link token requested. The prospect's
   * next action is identical either way, and telling them apart would put Plaid's product
   * vocabulary on a family's screen. See #1014's plan comment, open decision 3.
   */
  statementsUnavailable: z.boolean().optional(),
});

export type IntakeSnapshotAccount = z.infer<typeof IntakeSnapshotAccountSchema>;

/** ONE investment holding, as the snapshot records it. */
export const IntakeSnapshotHoldingSchema = z.strictObject({
  /** The account this position sits in. Matches an `accountId` above when the pull produced
   *  both; a holding whose account is absent is still shown, ungrouped. */
  accountId: SnapshotAccountId,
  name: SnapshotText,
  /** The ticker where the security has one. Absent for cash-equivalents and private funds. */
  symbol: z.string().max(24).regex(/^[^\u0000-\u001F\u007F-\u009F\uFFFE\uFFFF\s]*$/u).optional(),
  quantity: SnapshotAmount,
  /** The position's value as the institution reported it. `null` where none was supplied. */
  value: SnapshotAmount.nullable(),
  currency: SnapshotCurrency.optional(),
});

export type IntakeSnapshotHolding = z.infer<typeof IntakeSnapshotHoldingSchema>;

/**
 * The whole snapshot, as it rides a `save` message and sits on the draft row.
 *
 * 🚨 **One snapshot per draft, ACCUMULATED across links rather than replaced.** A prospect may
 * connect two banks; the SPA merges the new institution's accounts and holdings into the
 * confirmed snapshot before saving, keying accounts on `accountId`, exactly as it merges
 * statement records. The writer stores what the message carries and carries the stored value
 * forward when the field is absent, which is the same rule `statements` follows and for the
 * same reason: a tab whose link panel never rendered still autosaves.
 *
 * ⚠️ **`asOf` is the reader's clock, not the browser's**, and it is the only timestamp here.
 * It says when the pull happened, which is the one thing an adviser reading a balance needs to
 * know. It is not authority for anything and nothing orders by it.
 */
export const IntakeDraftSnapshotSchema = z.strictObject({
  /** When the reader took the pull, ISO 8601. */
  asOf: z.iso.datetime().max(64),
  /**
   * The provider, spelled out rather than assumed.
   *
   * A literal rather than a free string: today there is exactly one, and a snapshot from
   * somewhere else is a different contract with different guarantees about what was torn down
   * afterwards. Widening this is a design change, not a data change.
   */
  provider: z.literal('plaid'),
  /** The institutions this snapshot covers, in the order they were linked. */
  institutions: z.array(SnapshotText).max(INTAKE_SNAPSHOT_MAX_INSTITUTIONS),
  accounts: z.array(IntakeSnapshotAccountSchema).max(INTAKE_SNAPSHOT_MAX_ACCOUNTS),
  holdings: z.array(IntakeSnapshotHoldingSchema).max(INTAKE_SNAPSHOT_MAX_HOLDINGS),
  /**
   * The institution reported more positions than {@link INTAKE_SNAPSHOT_MAX_HOLDINGS}, so this
   * list is the first of them.
   *
   * Said out loud rather than left to be inferred from a round number, on the same principle
   * as #1029's truncation markers: an adviser reading a hundred positions must be able to tell
   * "this is the portfolio" from "this is the beginning of the portfolio".
   */
  holdingsTruncated: z.boolean().optional(),
});

export type IntakeDraftSnapshot = z.infer<typeof IntakeDraftSnapshotSchema>;
